import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Dataset, ExportOptions, ExportResult } from '@/lib/types';
import {
  convertToCSV,
  convertToJSON,
  convertToJupyterNotebook,
  convertToRScript,
  convertToXML,
  createDownloadLink,
  estimateFileSize,
  generateFileName,
  getMimeType,
} from '@/lib/exportUtils';
import {
  Archive,
  ArrowDown,
  ChartBar,
  CheckCircle,
  Code,
  Database,
  FileCsv,
  FileText,
  Info,
  Spinner,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface DataExportProps {
  datasets: Dataset[];
  onExport?: (options: ExportOptions) => Promise<ExportResult>;
}

interface ExportFormat {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  fileExtension: string;
  supportsCompression: boolean;
  bestFor: string[];
}

const exportFormats: ExportFormat[] = [
  {
    id: 'csv',
    name: 'CSV',
    description: 'Comma-separated values, universal compatibility',
    icon: <FileCsv className='w-5 h-5' />,
    fileExtension: '.csv',
    supportsCompression: true,
    bestFor: ['Excel', 'Spreadsheet apps', 'Data analysis tools'],
  },
  {
    id: 'json',
    name: 'JSON',
    description: 'JavaScript Object Notation, web-friendly',
    icon: <Code className='w-5 h-5' />,
    fileExtension: '.json',
    supportsCompression: true,
    bestFor: ['Web applications', 'APIs', 'JavaScript tools'],
  },
  {
    id: 'jupyter',
    name: 'Jupyter Notebook',
    description: 'Interactive Python notebook with data analysis code',
    icon: <Code className='w-5 h-5' />,
    fileExtension: '.ipynb',
    supportsCompression: false,
    bestFor: [
      'Python analysis',
      'Data science',
      'Machine learning',
      'Interactive exploration',
    ],
  },
  {
    id: 'r-script',
    name: 'R Script',
    description: 'R analysis script with data loading and visualization',
    icon: <ChartBar className='w-5 h-5' />,
    fileExtension: '.R',
    supportsCompression: false,
    bestFor: [
      'R analysis',
      'Statistical modeling',
      'Academic research',
      'Data visualization',
    ],
  },
  {
    id: 'xml',
    name: 'XML',
    description: 'Extensible Markup Language, structured data',
    icon: <Code className='w-5 h-5' />,
    fileExtension: '.xml',
    supportsCompression: true,
    bestFor: ['Enterprise systems', 'Data interchange', 'SOAP APIs'],
  },
  {
    id: 'parquet',
    name: 'Parquet',
    description: 'Columnar storage, optimized for analytics',
    icon: <Database className='w-5 h-5' />,
    fileExtension: '.parquet',
    supportsCompression: false,
    bestFor: ['Big data tools', 'Apache Spark', 'Data warehouses'],
  },
  {
    id: 'excel',
    name: 'Excel',
    description: 'Microsoft Excel workbook with multiple sheets',
    icon: <FileText className='w-5 h-5' />,
    fileExtension: '.xlsx',
    supportsCompression: false,
    bestFor: ['Business reports', 'Data presentations', 'Microsoft Office'],
  },
  {
    id: 'sqlite',
    name: 'SQLite',
    description: 'Lightweight SQL database file',
    icon: <Database className='w-5 h-5' />,
    fileExtension: '.db',
    supportsCompression: false,
    bestFor: ['SQL queries', 'Relational analysis', 'Database tools'],
  },
];

export function DataExport({ datasets, onExport }: Readonly<DataExportProps>) {
  const [selectedFormat, setSelectedFormat] = useState<string>('csv');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeSchema, setIncludeSchema] = useState(true);
  const [compressionLevel, setCompressionLevel] = useState<
    'none' | 'low' | 'medium' | 'high'
  >('medium');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [lastExport, setLastExport] = useState<ExportResult | null>(null);

  const selectedFormatData = exportFormats.find(f => f.id === selectedFormat);
  const totalRows = datasets.reduce(
    (sum, dataset) => sum + dataset.totalRows,
    0
  );
  const totalFields = datasets.reduce(
    (sum, dataset) => sum + dataset.schema.length,
    0
  );
  const estimatedSize = selectedFormatData
    ? estimateFileSize(datasets, selectedFormat)
    : 0;

  const handleExport = async () => {
    if (datasets.length === 0) {
      toast.error('No datasets selected for export');

      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    const options: ExportOptions = {
      format: selectedFormat as any,
      includeMetadata,
      includeSchema,
      compressionLevel: selectedFormatData?.supportsCompression
        ? compressionLevel
        : undefined,
    };

    try {
      // Simulate export progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 80) {
            clearInterval(progressInterval);

            return prev;
          }

          return prev + Math.random() * 15;
        });
      }, 150);

      // Generate the actual export content
      let content: string = '';
      let mimeType: string = getMimeType(selectedFormat);

      switch (selectedFormat) {
        case 'csv':
          content = convertToCSV(datasets, options);
          break;
        case 'json':
          content = convertToJSON(datasets, options);
          break;
        case 'xml':
          content = convertToXML(datasets, options);
          break;
        case 'jupyter':
          content = convertToJupyterNotebook(datasets, options);
          break;
        case 'r-script':
          content = convertToRScript(datasets, options);
          break;
        case 'parquet':
        case 'excel':
        case 'sqlite':
          // These would require more complex libraries in a real implementation
          content = `# ${selectedFormat.toUpperCase()} Export\n# This format requires specialized libraries for full implementation\n# Showing sample data in text format:\n\n${convertToCSV(datasets, options)}`;
          mimeType = 'text/plain';
          break;
        default:
          content = convertToJSON(datasets, options);
      }

      clearInterval(progressInterval);
      setExportProgress(90);

      // Generate filename and prepare result
      const filename = generateFileName(selectedFormat, datasets);
      const actualSize = new Blob([content]).size;

      const result: ExportResult = {
        filename,
        format: selectedFormat,
        size: actualSize,
        downloadUrl: '#',
        metadata: {
          exportedAt: new Date().toISOString(),
          totalRows,
          totalFields,
          datasetsIncluded: datasets.map(d => d.name),
        },
      };

      setLastExport(result);
      setExportProgress(100);

      if (onExport) {
        await onExport(options);
      }

      toast.success(
        `Export completed! ${datasets.length} dataset${datasets.length !== 1 ? 's' : ''} exported as ${selectedFormatData?.name}`
      );

      // Trigger actual download
      setTimeout(() => {
        createDownloadLink(content, filename, mimeType);
        toast.info('Download started');
      }, 500);
    } catch (error) {
      toast.error('Export failed. Please try again.');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(0), 2000);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className='space-y-6'>
      {/* Export Summary */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <ArrowDown className='w-5 h-5' />
            Export Summary
          </CardTitle>
          <CardDescription>
            Export {datasets.length} selected dataset
            {datasets.length !== 1 ? 's' : ''} in your preferred format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-center'>
            <div>
              <div className='text-2xl font-bold text-primary'>
                {datasets.length}
              </div>
              <div className='text-sm text-muted-foreground'>Datasets</div>
            </div>
            <div>
              <div className='text-2xl font-bold text-primary'>
                {totalRows.toLocaleString()}
              </div>
              <div className='text-sm text-muted-foreground'>Total Rows</div>
            </div>
            <div>
              <div className='text-2xl font-bold text-primary'>
                {totalFields}
              </div>
              <div className='text-sm text-muted-foreground'>Total Fields</div>
            </div>
            <div>
              <div className='text-2xl font-bold text-primary'>
                {formatFileSize(estimatedSize)}
              </div>
              <div className='text-sm text-muted-foreground'>Est. Size</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Format Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Export Format</CardTitle>
          <CardDescription>
            Choose the format that best suits your intended use case
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {exportFormats.map(format => (
              <button
                key={format.id}
                type='button'
                className={`w-full p-4 border rounded-lg transition-all hover:shadow-sm text-left ${
                  selectedFormat === format.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
                onClick={() => setSelectedFormat(format.id)}
              >
                <div className='flex items-start gap-3'>
                  <div
                    className={`p-2 rounded ${
                      selectedFormat === format.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {format.icon}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-semibold'>{format.name}</h3>
                      {selectedFormat === format.id && (
                        <CheckCircle className='w-4 h-4 text-primary' />
                      )}
                    </div>
                    <p className='text-sm text-muted-foreground mb-2'>
                      {format.description}
                    </p>
                    <div className='space-y-1'>
                      <div className='text-xs font-medium text-muted-foreground'>
                        Best for:
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {format.bestFor.slice(0, 2).map(use => (
                          <Badge
                            key={use}
                            variant='outline'
                            className='text-xs'
                          >
                            {use}
                          </Badge>
                        ))}
                        {format.bestFor.length > 2 && (
                          <Badge variant='outline' className='text-xs'>
                            +{format.bestFor.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card>
        <CardHeader>
          <CardTitle>Export Options</CardTitle>
          <CardDescription>
            Customize your export with additional data and settings
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Data Options */}
            <div className='space-y-4'>
              <h4 className='font-semibold'>Data Options</h4>

              <div className='flex items-center justify-between'>
                <div className='space-y-0.5'>
                  <Label htmlFor='include-metadata'>Include Metadata</Label>
                  <div className='text-sm text-muted-foreground'>
                    Export dataset descriptions, source info, and timestamps
                  </div>
                </div>
                <Switch
                  id='include-metadata'
                  checked={includeMetadata}
                  onCheckedChange={setIncludeMetadata}
                />
              </div>

              <div className='flex items-center justify-between'>
                <div className='space-y-0.5'>
                  <Label htmlFor='include-schema'>Include Schema</Label>
                  <div className='text-sm text-muted-foreground'>
                    Export field types, descriptions, and validation rules
                  </div>
                </div>
                <Switch
                  id='include-schema'
                  checked={includeSchema}
                  onCheckedChange={setIncludeSchema}
                />
              </div>
            </div>

            {/* Format Options */}
            <div className='space-y-4'>
              <h4 className='font-semibold'>Format Options</h4>

              {selectedFormatData?.supportsCompression && (
                <div className='space-y-2'>
                  <Label htmlFor='compression'>Compression Level</Label>
                  <Select
                    value={compressionLevel}
                    onValueChange={(value: any) => setCompressionLevel(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>None (Faster)</SelectItem>
                      <SelectItem value='low'>Low</SelectItem>
                      <SelectItem value='medium'>
                        Medium (Recommended)
                      </SelectItem>
                      <SelectItem value='high'>High (Smaller file)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className='text-sm text-muted-foreground'>
                    Higher compression reduces file size but increases
                    processing time
                  </div>
                </div>
              )}

              {selectedFormat === 'excel' && (
                <div className='flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200'>
                  <Info className='w-4 h-4 text-blue-600 mt-0.5' />
                  <div className='text-sm'>
                    <div className='font-medium text-blue-900'>
                      Excel Export
                    </div>
                    <div className='text-blue-700'>
                      Each dataset will be exported as a separate worksheet
                    </div>
                  </div>
                </div>
              )}

              {selectedFormat === 'jupyter' && (
                <div className='flex items-start gap-2 p-3 rounded-lg bg-purple-50 border border-purple-200'>
                  <Info className='w-4 h-4 text-purple-600 mt-0.5' />
                  <div className='text-sm'>
                    <div className='font-medium text-purple-900'>
                      Jupyter Notebook Export
                    </div>
                    <div className='text-purple-700'>
                      Complete Python analysis notebook with data loading,
                      exploration, and visualization code
                    </div>
                  </div>
                </div>
              )}

              {selectedFormat === 'r-script' && (
                <div className='flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200'>
                  <Info className='w-4 h-4 text-green-600 mt-0.5' />
                  <div className='text-sm'>
                    <div className='font-medium text-green-900'>
                      R Script Export
                    </div>
                    <div className='text-green-700'>
                      Ready-to-run R analysis script with data manipulation,
                      visualization, and statistical analysis code
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Progress */}
      {isExporting && (
        <Card>
          <CardContent className='pt-6'>
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Exporting data...</span>
                <span className='text-sm text-muted-foreground'>
                  {Math.round(exportProgress)}%
                </span>
              </div>
              <Progress value={exportProgress} className='w-full' />
              <div className='text-xs text-muted-foreground'>
                Processing {datasets.length} dataset
                {datasets.length !== 1 ? 's' : ''} in {selectedFormatData?.name}{' '}
                format
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last Export Result */}
      {lastExport && !isExporting && (
        <Card className='border-green-200 bg-green-50'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2 text-green-900'>
              <CheckCircle className='w-5 h-5' />
              Export Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
              <div>
                <div className='font-medium text-green-900'>Filename</div>
                <div className='text-green-700 font-mono'>
                  {lastExport.filename}
                </div>
              </div>
              <div>
                <div className='font-medium text-green-900'>Format</div>
                <div className='text-green-700'>
                  {lastExport.format.toUpperCase()}
                </div>
              </div>
              <div>
                <div className='font-medium text-green-900'>File Size</div>
                <div className='text-green-700'>
                  {formatFileSize(lastExport.size)}
                </div>
              </div>
              <div>
                <div className='font-medium text-green-900'>Exported At</div>
                <div className='text-green-700'>
                  {new Date(
                    lastExport.metadata.exportedAt
                  ).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Action */}
      <div className='flex justify-center'>
        <Button
          onClick={handleExport}
          disabled={datasets.length === 0 || isExporting}
          size='lg'
          className='gap-2 min-w-[200px]'
        >
          {isExporting ? (
            <>
              <Spinner className='w-5 h-5 animate-spin' />
              Exporting...
            </>
          ) : (
            <>
              <ArrowDown className='w-5 h-5' />
              Export {datasets.length} Dataset{datasets.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>

      {datasets.length === 0 && (
        <div className='text-center py-8'>
          <Archive className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
          <h3 className='text-xl font-semibold mb-2'>No Datasets Selected</h3>
          <p className='text-muted-foreground'>
            Select datasets from the Discover or Browse tabs to enable export
            functionality
          </p>
        </div>
      )}
    </div>
  );
}
